import React, { useEffect, useRef } from 'react'
import {
  Text,
  IconSparkles,
  GreyGrey0,
  GreyGrey20,
  GreyGrey90,
  IndigoIndigo50,
  Primary13Regular,
  ShadowL,
  Box,
  Flex
} from '@sequencehq/core-components'

interface TableContextMenuProps {
  isOpen: boolean
  position: { x: number; y: number }
  onClose: () => void
  onAddToChat: () => void
  selectionInfo?: {
    rowCount: number
    columnCount: number
    rowLabels: string[]
    columnLabels: string[]
  }
}

export const TableContextMenu: React.FC<TableContextMenuProps> = ({
  isOpen,
  position,
  onClose,
  onAddToChat,
  selectionInfo
}) => {
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  // Adjust position to keep menu in viewport
  const adjustedPosition = React.useMemo(() => {
    if (!isOpen) return position

    const menuWidth = 200
    const menuHeight = 60
    const padding = 10

    let { x, y } = position

    // Adjust horizontal position
    if (x + menuWidth > window.innerWidth - padding) {
      x = window.innerWidth - menuWidth - padding
    }
    if (x < padding) {
      x = padding
    }

    // Adjust vertical position
    if (y + menuHeight > window.innerHeight - padding) {
      y = window.innerHeight - menuHeight - padding
    }
    if (y < padding) {
      y = padding
    }

    return { x, y }
  }, [position, isOpen])

  if (!isOpen) return null

  const handleAddToChat = () => {
    onAddToChat()
    onClose()
  }

  const getSelectionDescription = () => {
    if (!selectionInfo) return undefined

    const { rowCount, columnCount, rowLabels, columnLabels } = selectionInfo

    if (rowCount === 1 && columnCount === 1) {
      return `${rowLabels[0]} - ${columnLabels[0]}`
    } else if (rowCount === 1) {
      return `${rowLabels[0]} (${columnCount} columns)`
    } else if (columnCount === 1) {
      return `${columnLabels[0]} (${rowCount} rows)`
    }

    return `${rowCount} rows × ${columnCount} columns`
  }

  const buttonAriaLabel = selectionInfo
    ? `Ask AI about ${getSelectionDescription()}`
    : 'Ask AI'

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      onClose()
    }
  }

  return (
    <Box
      ref={menuRef}
      position="fixed"
      left={`${adjustedPosition.x}px`}
      top={`${adjustedPosition.y}px`}
      zIndex={9999}
      backgroundColor={GreyGrey0}
      borderRadius="8px"
      border={`1px solid ${GreyGrey20}`}
      boxShadow={ShadowL}
      minWidth="220px"
      data-testid="revrec.waterfall.table.row.contextMenu"
    >
      <Flex padding={2} gap={1} direction="column">
        <Flex
          as="button"
          type="button"
          alignItems="center"
          gap={3}
          padding={2}
          borderRadius={6}
          cursor="pointer"
          background={GreyGrey0}
          border="none"
          width="100%"
          textAlign="left"
          _hover={{ background: GreyGrey20 }}
          _focusVisible={{
            outline: `2px solid ${IndigoIndigo50}`,
            outlineOffset: '2px'
          }}
          onClick={handleAddToChat}
          onKeyDown={handleKeyDown}
          aria-label={buttonAriaLabel}
          data-testid="revrec.waterfall.table.row.contextMenu.ask"
        >
          <Flex align="center" justify="center" shrink={0}>
            <IconSparkles color={IndigoIndigo50} size={18} />
          </Flex>
          <Flex direction="column" align="flex-start" gap="2px">
            <Text {...Primary13Regular} color={GreyGrey90}>
              Ask AI
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  )
}
